import classnames from 'classnames';
import { DocumentNode } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';

import {
  getHashParamsFromLineNumber,
  mapLineNumsToTypes,
} from '../../../schemaPage/sdlEditorHelpers';

export const TypeMap = ({
  typeDefs,
  selectedSchema,
  editor,
}: {
  typeDefs: DocumentNode;
  selectedSchema?: string;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
}) => {
  const { locationToSDL } = useInternalGraphLinking();

  const history = useHistory();

  const findCenterLine = React.useCallback(() => {
    const visibleRange = editor.getVisibleRanges()[0];

    if (!visibleRange) {
      return 0;
    }
    const visibleDiff = Math.floor(
      (visibleRange.endLineNumber - visibleRange.startLineNumber) / 2,
    );
    const currentCenter = visibleRange.startLineNumber + visibleDiff;
    return currentCenter;
  }, [editor]);

  const { lineNumbersToTypeNames, definitionTypeWithLocation } = React.useMemo(
    () => mapLineNumsToTypes(typeDefs),
    [typeDefs],
  );

  const [centeredType, setCenteredType] = React.useState<string>();

  React.useEffect(() => {
    return editor.onDidScrollChange(() => {
      const visibleRange = editor.getVisibleRanges()[0];
      const lastLineNum = editor.getModel()?.getLineCount();

      if (lastLineNum === undefined || visibleRange === undefined) {
        return;
      }

      const centerLine = findCenterLine();
      const currentScrollPos = editor.getScrollTop();

      const typeNames = Object.values(lineNumbersToTypeNames);
      const firstType = typeNames[0];
      const lastType = typeNames[typeNames.length - 1];

      if (currentScrollPos === 0) {
        setCenteredType(firstType);
      } else if (
        visibleRange.startLineNumber <= lastLineNum &&
        lastLineNum <= visibleRange.endLineNumber
      ) {
        setCenteredType(lastType);
      } else if (lineNumbersToTypeNames[centerLine]) {
        setCenteredType(lineNumbersToTypeNames[centerLine]);
      }
    }).dispose;
  }, [
    definitionTypeWithLocation.length,
    editor,
    findCenterLine,
    lineNumbersToTypeNames,
  ]);

  const handleOnLinkClick = React.useCallback(
    (lineNumber: number | undefined) => {
      const sdlHashParams = getHashParamsFromLineNumber(typeDefs, lineNumber);
      if (sdlHashParams) {
        const newLocation = locationToSDL({
          selectedSchema,
          graphqlTypeName: sdlHashParams.graphqlTypeName,
          fieldName: sdlHashParams.fieldName,
          argumentName: sdlHashParams.argumentName,
        });

        history.push(newLocation);
        if (lineNumber !== undefined) {
          editor.revealLineInCenter(lineNumber);
        }
        setCenteredType(sdlHashParams.graphqlTypeName);
      }
    },
    [selectedSchema, editor, locationToSDL, history, typeDefs],
  );

  return (
    <div
      className={classnames(
        'group absolute right-0 top-16 z-10 mr-7 w-9 max-w-72 cursor-pointer overflow-hidden rounded-l border-y border-l border-r-4 border-solid border-transparent pr-1 transition-all delay-[250ms] duration-100',
        'hover:w-full hover:cursor-default hover:overflow-y-auto hover:border-y-primary hover:border-l-primary hover:border-r-info hover:bg-secondary hover:shadow-md hover:delay-200',
        // avoid overlapping with the ui elements above
        // and below this typemap, which have heights set via rem values
        'max-h-[calc(100%-4rem-4rem)]',
        definitionTypeWithLocation.length > 2 ? 'visible' : 'invisible',
      )}
    >
      <div
        className={classnames(
          'size-full translate-x-[10%] transition-all delay-[250ms] duration-100 ease-in-out will-change-transform',
          'group-hover:translate-x-0 group-hover:transition-transform group-hover:delay-200',
        )}
      >
        <div className="overflow-y-auto pl-3">
          <div className="relative flex-1 overflow-y-auto py-2">
            <div className="absolute inset-y-5 left-1 ml-1 w-0.5 bg-selected" />
            {definitionTypeWithLocation.map((type) => (
              <div
                className="flex w-full max-w-[17rem] cursor-pointer items-center pl-1"
                key={type.lineNum}
                onClick={() => {
                  handleOnLinkClick(type.lineNum);
                }}
              >
                <div
                  className={classnames(
                    'relative size-2.5 flex-none rounded-full bg-border-info',
                    centeredType === type.typeName &&
                      definitionTypeWithLocation.length > 2
                      ? 'visible'
                      : 'invisible',
                  )}
                />
                <div
                  title={type.typeName}
                  className={classnames(
                    'mx-2 inline-block flex-1 cursor-pointer truncate break-all rounded-md px-4 py-2 font-mono text-sm opacity-0 transition-opacity delay-[250ms] duration-100 ease-in-out hover:bg-btn-secondary-hover hover:font-semibold hover:text-primary hover:delay-[0ms] group-hover:opacity-100 group-hover:delay-[0ms]',
                    centeredType === type.typeName
                      ? 'bg-neutral font-semibold'
                      : 'text-primary',
                  )}
                >
                  {type.typeName}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
